interface UserAction {
  id: string;
  type: 'click' | 'scroll' | 'input' | 'navigation' | 'error';
  timestamp: number;
  element?: string;
  page: string;
  duration?: number;
  metadata?: any;
}

interface UXMetrics {
  sessionDuration: number;
  pageViews: number;
  errorRate: number;
  avgTaskTime: number;
  bounceRate: number;
  engagementScore: number;
}

class UserExperienceTracker {
  private actions: UserAction[] = [];
  private sessionStart = Date.now();
  private currentPage = window.location.pathname;
  private pageStartTime = Date.now();
  private isTracking = true;

  constructor() {
    this.initializeTracking();
  }

  private initializeTracking(): void {
    // Click tracking
    document.addEventListener('click', (event) => {
      if (!this.isTracking) return;
      
      const target = event.target as HTMLElement;
      this.recordAction('click', {
        element: this.getElementSelector(target),
        coordinates: { x: event.clientX, y: event.clientY }
      });
    });

    // Scroll tracking (throttled)
    let scrollTimeout: NodeJS.Timeout;
    document.addEventListener('scroll', () => {
      if (!this.isTracking) return;
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.recordAction('scroll', {
          scrollY: window.scrollY,
          scrollPercent: (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
        });
      }, 250);
    });

    // Input tracking
    document.addEventListener('input', (event) => {
      if (!this.isTracking) return;
      
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        this.recordAction('input', {
          element: this.getElementSelector(target),
          inputType: (target as HTMLInputElement).type
        });
      }
    });

    // Navigation tracking
    window.addEventListener('beforeunload', () => {
      this.recordPageExit();
    });

    // Error tracking
    window.addEventListener('error', (event) => {
      this.recordAction('error', {
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        column: event.colno
      });
    });

    // Visibility change tracking
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseTracking();
      } else {
        this.resumeTracking();
      }
    });
  }

  recordAction(type: UserAction['type'], metadata: any = {}): void {
    if (!this.isTracking) return;

    const action: UserAction = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      timestamp: Date.now(),
      page: this.currentPage,
      metadata
    };

    this.actions.push(action);
    this.maintainActionsSize();
  }

  recordNavigation(to: string): void {
    // Record page exit
    this.recordPageExit();
    
    // Update current page
    this.currentPage = to;
    this.pageStartTime = Date.now();
    
    // Record navigation
    this.recordAction('navigation', {
      from: this.currentPage,
      to: to
    });
  }

  private recordPageExit(): void {
    const timeOnPage = Date.now() - this.pageStartTime;
    this.recordAction('navigation', {
      type: 'page_exit',
      timeOnPage,
      page: this.currentPage
    });
  }

  getUXMetrics(): UXMetrics {
    const now = Date.now();
    const sessionDuration = now - this.sessionStart;
    
    const navigationActions = this.actions.filter(a => a.type === 'navigation');
    const errorActions = this.actions.filter(a => a.type === 'error');
    const clickActions = this.actions.filter(a => a.type === 'click');
    
    return {
      sessionDuration: sessionDuration / 1000, // seconds
      pageViews: navigationActions.length,
      errorRate: errorActions.length / Math.max(1, this.actions.length),
      avgTaskTime: this.calculateAvgTaskTime(),
      bounceRate: this.calculateBounceRate(),
      engagementScore: this.calculateEngagementScore()
    };
  }

  private calculateAvgTaskTime(): number {
    const taskActions = this.actions.filter(a => 
      a.type === 'click' && a.metadata?.element?.includes('button')
    );
    
    if (taskActions.length < 2) return 0;
    
    let totalTime = 0;
    let taskCount = 0;
    
    for (let i = 1; i < taskActions.length; i++) {
      const timeBetween = taskActions[i].timestamp - taskActions[i-1].timestamp;
      if (timeBetween < 30000) { // Less than 30 seconds = likely same task
        totalTime += timeBetween;
        taskCount++;
      }
    }
    
    return taskCount > 0 ? totalTime / taskCount / 1000 : 0; // seconds
  }

  private calculateBounceRate(): number {
    const navigationActions = this.actions.filter(a => a.type === 'navigation');
    const timeOnFirstPage = navigationActions.length > 0 ? 
      navigationActions[0].metadata?.timeOnPage || 0 : 
      Date.now() - this.sessionStart;
    
    // Bounce if less than 30 seconds on first page and no other interactions
    const hasInteractions = this.actions.some(a => 
      a.type === 'click' || a.type === 'input'
    );
    
    return (timeOnFirstPage < 30000 && !hasInteractions) ? 1 : 0;
  }

  private calculateEngagementScore(): number {
    const metrics = {
      clicks: this.actions.filter(a => a.type === 'click').length,
      inputs: this.actions.filter(a => a.type === 'input').length,
      scrolls: this.actions.filter(a => a.type === 'scroll').length,
      timeSpent: (Date.now() - this.sessionStart) / 1000 / 60, // minutes
      pageViews: this.actions.filter(a => a.type === 'navigation').length
    };
    
    // Weighted engagement score (0-100)
    let score = 0;
    score += Math.min(metrics.clicks * 2, 20); // Max 20 points for clicks
    score += Math.min(metrics.inputs * 5, 25); // Max 25 points for inputs
    score += Math.min(metrics.scrolls * 0.5, 10); // Max 10 points for scrolls
    score += Math.min(metrics.timeSpent * 2, 30); // Max 30 points for time
    score += Math.min(metrics.pageViews * 3, 15); // Max 15 points for page views
    
    return Math.min(100, score);
  }

  getUserJourney(): UserAction[] {
    return this.actions
      .filter(a => a.type === 'navigation' || a.type === 'click')
      .sort((a, b) => a.timestamp - b.timestamp);
  }

  getErrorPatterns(): { pattern: string; count: number; lastOccurrence: number }[] {
    const errorActions = this.actions.filter(a => a.type === 'error');
    const patterns = new Map<string, { count: number; lastOccurrence: number }>();
    
    errorActions.forEach(action => {
      const pattern = `${action.metadata?.message || 'Unknown'} at ${action.page}`;
      const existing = patterns.get(pattern) || { count: 0, lastOccurrence: 0 };
      patterns.set(pattern, {
        count: existing.count + 1,
        lastOccurrence: Math.max(existing.lastOccurrence, action.timestamp)
      });
    });
    
    return Array.from(patterns.entries()).map(([pattern, data]) => ({
      pattern,
      ...data
    }));
  }

  private getElementSelector(element: HTMLElement): string {
    if (element.id) return `#${element.id}`;
    if (element.className && typeof element.className === 'string') return `.${element.className.split(' ')[0]}`;
    return element.tagName.toLowerCase();
  }

  private maintainActionsSize(): void {
    if (this.actions.length > 1000) {
      this.actions = this.actions.slice(-1000);
    }
  }

  pauseTracking(): void {
    this.isTracking = false;
  }

  resumeTracking(): void {
    this.isTracking = true;
  }

  exportData(): string {
    return JSON.stringify({
      session: {
        start: this.sessionStart,
        duration: Date.now() - this.sessionStart
      },
      metrics: this.getUXMetrics(),
      actions: this.actions,
      journey: this.getUserJourney(),
      errors: this.getErrorPatterns()
    }, null, 2);
  }

  clearData(): void {
    this.actions = [];
    this.sessionStart = Date.now();
  }
}

export const userExperienceTracker = new UserExperienceTracker();
export type { UserAction, UXMetrics };
